/**
 * useMobileData - Hook for managing mobile emulator data
 *
 * Handles: Hospitais, locais, sync, offline data
 */
import { useState, useEffect, useCallback } from 'react';
import mobileService, { Location } from '../services/mobileService';
import { Hospital } from '../services/hospitalService';
import authService from '../services/authService';
import { storage } from '../adapters/storage';
import { addDebugLog, initializeDebugLogging } from '../components/DebugPanel';
import { getDevLoginCredentials } from '../config/devLogin';

export type MobileScreen = 'sync' | 'hospital' | 'local' | 'counting' | 'scanner';

export interface UseMobileDataReturn {
  // Connectivity
  isOnline: boolean;
  setIsOnline: (online: boolean) => void;

  // Navigation
  currentScreen: MobileScreen;
  setCurrentScreen: (screen: MobileScreen) => void;

  // Data
  hospitais: Hospital[];
  locais: Location[];
  selectedHospital: Hospital | null;
  setSelectedHospital: (hospital: Hospital | null) => void;
  selectedLocal: Location | null;
  setSelectedLocal: (local: Location | null) => void;
  currentJobId: number | null;
  setCurrentJobId: (jobId: number | null) => void;

  // Loading states
  isSyncing: boolean;
  loadingHospitais: boolean;
  loadingLocais: boolean;
  error: string;
  setError: (error: string) => void;

  // Operations
  handleSync: () => Promise<void>;
  handleSelectHospital: (hospital: Hospital) => Promise<void>;
  handleSelectLocal: (local: Location) => Promise<void>;
}

export const useMobileData = (): UseMobileDataReturn => {
  // Connectivity
  const [isOnline, setIsOnline] = useState(true);

  // Navigation
  const [currentScreen, setCurrentScreen] = useState<MobileScreen>('sync');

  // Data
  const [hospitais, setHospitais] = useState<Hospital[]>([]);
  const [locais, setLocais] = useState<Location[]>([]);
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null);
  const [selectedLocal, setSelectedLocal] = useState<Location | null>(null);
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);

  // Loading states
  const [isSyncing, setIsSyncing] = useState(false);
  const [loadingHospitais, setLoadingHospitais] = useState(false);
  const [loadingLocais, setLoadingLocais] = useState(false);
  const [error, setError] = useState<string>('');

  // Initialize debug logging and load offline data on mount
  useEffect(() => {
    initializeDebugLogging();

    addDebugLog({
      level: 'info',
      category: 'system',
      message: '🚀 Mobile Emulator App initialized',
      data: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        timestamp: new Date().toISOString(),
        version: '1.0.0'
      }
    });

    const loadOfflineData = async () => {
      const hospitaisOffline = await storage.getItem('hospitais_offline');
      if (hospitaisOffline) {
        try {
          const data = JSON.parse(hospitaisOffline);
          setHospitais(data);
          addDebugLog({
            level: 'info',
            category: 'storage',
            message: `📱 Offline data loaded: ${data.length} hospitals`,
            data: { hospitalCount: data.length, dataSize: hospitaisOffline.length }
          });
        } catch (e: any) {
          addDebugLog({
            level: 'error',
            category: 'storage',
            message: 'Error loading offline data',
            data: { error: e.message, stack: e.stack }
          });
        }
      }
    };
    loadOfflineData();
  }, []);

  // Handler: Sincronizar dados
  const handleSync = useCallback(async () => {
    if (!isOnline) return;

    setIsSyncing(true);
    setError('');

    try {
      const token = await storage.getItem('accessToken');

      if (!token) {
        // ⛔ SEGURANÇA (29/07): a credencial de um usuário ADMIN estava ESCRITA
        // aqui e por isso ia no bundle publicado —
        // confirmado baixando o assets/index-*.js do ar. Agora vem de
        // VITE_DEV_LOGIN_* e some do build de produção. Ver config/devLogin.ts.
        const devLogin = getDevLoginCredentials();
        if (devLogin) await authService.login(devLogin);
      }

      const hospitaisData = await mobileService.getHospitals();
      setHospitais(hospitaisData);

      await storage.setItem('hospitais_offline', JSON.stringify(hospitaisData));

      setCurrentScreen('hospital');
    } catch (err: any) {
      console.error('❌ Erro ao sincronizar:', err);
      setError(err.message || 'Erro ao sincronizar dados');
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline]);

  // Handler: Selecionar hospital
  const handleSelectHospital = useCallback(async (hospital: Hospital) => {
    setSelectedHospital(hospital);
    setLoadingLocais(true);
    setError('');

    try {
      if (isOnline) {
        const locaisData = await mobileService.getLocations(hospital.id);
        setLocais(locaisData);
        await storage.setItem(`locais_offline_${hospital.id}`, JSON.stringify(locaisData));
      } else {
        const locaisOffline = await storage.getItem(`locais_offline_${hospital.id}`);
        if (locaisOffline) {
          const data = JSON.parse(locaisOffline);
          setLocais(data);
        } else {
          throw new Error('Nenhum dado offline disponível para este hospital.');
        }
      }
      setCurrentScreen('local');
    } catch (err: any) {
      console.error('Erro ao carregar locais:', err);
      setError(err.message || 'Erro ao carregar locais');
    } finally {
      setLoadingLocais(false);
    }
  }, [isOnline]);

  // Handler: Selecionar local e criar job
  const handleSelectLocal = useCallback(async (local: Location) => {
    if (!selectedHospital) return;

    setSelectedLocal(local);
    setError('');

    try {
      const jobData = {
        hospital_id: selectedHospital.id,
        location_id: local.id,
        operador_id: 18,
        titulo: `Contagem ${selectedHospital.nome} - ${local.nome}`,
        descricao: `Contagem realizada em ${new Date().toLocaleDateString('pt-BR')}`,
        data_prevista: new Date().toISOString().split('T')[0],
      };

      const job = await mobileService.createJob(jobData);
      setCurrentJobId(job.id);

      await mobileService.startJob(job.id);

      setCurrentScreen('counting');
    } catch (err: any) {
      console.error('Erro ao criar job:', err);
      setError(err.message || 'Erro ao criar job de contagem');
    }
  }, [selectedHospital]);

  return {
    isOnline,
    setIsOnline,
    currentScreen,
    setCurrentScreen,
    hospitais,
    locais,
    selectedHospital,
    setSelectedHospital,
    selectedLocal,
    setSelectedLocal,
    currentJobId,
    setCurrentJobId,
    isSyncing,
    loadingHospitais,
    loadingLocais,
    error,
    setError,
    handleSync,
    handleSelectHospital,
    handleSelectLocal,
  };
};

export type { Hospital, Location };
